import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Badge,
  Notice,
  Disclosure,
  Icon,
} from "@conuti-das/prince-ui";
import { Flex, Stack, Spinner, princeIcon } from "@/ui-prince";
import { mapperApi, type ApplyResult } from "@/api/mapperApi";
import { EdifactInput } from "../Validator/EdifactInput";
import { Bo4eJsonEditor } from "./Bo4eJsonEditor";

interface Props {
  bundleId: string;
  msgType: string;
}

/**
 * Testen-Tab analog zum Validator: EDIFACT links rein, BO4E-JSON rechts raus.
 * Der JSON-Editor ist schema-aware (BO4E v1): Autocomplete, Hover-Doku, Lint.
 */
export function MappingTestTab({ bundleId, msgType }: Props) {
  const [raw, setRaw] = useState("");
  const [includeReview, setIncludeReview] = useState(false);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [jsonText, setJsonText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [schemas, setSchemas] = useState<Record<string, Record<string, unknown>> | null>(null);

  // BO4E-Schema-Katalog einmal laden (für Editor-Autocomplete/Doku).
  useEffect(() => {
    mapperApi
      .getBo4eSchemas()
      .then((r) => setSchemas(r.schemas))
      .catch(() => setSchemas(null)); // Editor degradiert zu plain JSON
  }, []);

  async function handleApply() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const r = await mapperApi.apply(raw, msgType, {
        bundle: bundleId,
        includeNeedsReview: includeReview,
      });
      setResult(r);
      setJsonText(JSON.stringify(r.bo4e, null, 2));
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  const topLevelKeys = useMemo(
    () => (result ? Object.keys(result.bo4e) : []),
    [result],
  );

  const validationIssues = useMemo(() => {
    if (!result?.validation) return [];
    return Object.entries(result.validation).flatMap(([bo, v]) =>
      (v.errors ?? []).map((e) => ({ bo, ...e })),
    );
  }, [result]);

  return (
    <Stack style={{ gap: "var(--s-3)" }}>
      <Card>
        <Stack style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-3)" }}>
          <EdifactInput value={raw} onChange={setRaw} />
          <Flex style={{ alignItems: "center", gap: "var(--s-3)", flexWrap: "wrap" }}>
            <Button
              variant="filled"
              isDisabled={!raw.trim() || !msgType || busy}
              onPress={handleApply}
            >
              <Icon name={princeIcon("process")} size={16} />
              {busy ? "Wende Mapping an ..." : "Mapping anwenden (EDI → BO4E)"}
            </Button>
            <Checkbox
              isSelected={includeReview}
              onChange={setIncludeReview}
            >
              needs_review-Entries einbeziehen
            </Checkbox>
            {busy && <Spinner size="s" />}
            <span style={{ color: "var(--sapContent_LabelColor)", fontSize: "0.8rem" }}>
              {msgType} · Bundle {bundleId} · <code>POST /api/mapper/apply</code>
            </span>
          </Flex>
        </Stack>
      </Card>

      {error && (
        <Notice tone="negative">
          Fehler beim Anwenden: {error}
        </Notice>
      )}

      {result?.error && (
        <Notice tone="negative">
          {result.error}
        </Notice>
      )}

      {result && !result.error && (
        <>
          <Flex style={{ gap: "var(--s-2)", alignItems: "center", flexWrap: "wrap" }}>
            <Badge tone="teal">
              {result.message?.type} {result.message?.version} · {result.message?.segments} Segmente
            </Badge>
            <Badge tone="green">{result.stats.applied} angewendet</Badge>
            <Badge tone={result.stats.skipped > 0 ? "orange" : "neutral"}>
              {result.stats.skipped} übersprungen
            </Badge>
            {validationIssues.length > 0 ? (
              <Badge tone="red">
                {validationIssues.length} Schema-Verstöße
              </Badge>
            ) : result.validation ? (
              <Badge tone="green">Schema OK</Badge>
            ) : null}
            {!schemas && (
              <span style={{ color: "var(--sapContent_LabelColor)", fontSize: "0.8rem" }}>
                (BO4E-Schemas nicht verfügbar — Editor ohne Autocomplete)
              </span>
            )}
          </Flex>

          <Card>
            <Stack style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-2)" }}>
              <span style={{ fontWeight: 600 }}>
                BO4E-Ergebnis{" "}
                <span style={{ color: "var(--sapContent_LabelColor)", fontSize: "0.8rem" }}>
                  — editierbar, mit Schema-Autocomplete (Strg+Leertaste) und Hover-Doku
                </span>
              </span>
              <Bo4eJsonEditor
                value={jsonText}
                onChange={setJsonText}
                schemaCatalog={schemas}
                topLevelKeys={topLevelKeys}
              />
            </Stack>
          </Card>

          {validationIssues.length > 0 && (
            <Disclosure title={`Schema-Validierung: ${validationIssues.length} Verstöße`} defaultExpanded={false}>
              {validationIssues.map((v, i) => (
                <Flex key={i} style={{ gap: "var(--s-2)", padding: "2px 0" }}>
                  <Badge tone="orange">{v.bo}</Badge>
                  <span style={{ fontFamily: "var(--sapFontMonoFamily, monospace)", fontSize: "0.8rem" }}>
                    {v.path || "/"}: {v.message}
                  </span>
                </Flex>
              ))}
            </Disclosure>
          )}

          <Disclosure title={`Angewendet (${result.applied.length})`} defaultExpanded={false}>
            {result.applied.map((a) => (
              <Flex key={a.mapping_id} style={{ gap: "var(--s-2)", padding: "2px 0", flexWrap: "wrap" }}>
                <span style={{ fontFamily: "var(--sapFontMonoFamily, monospace)", fontSize: "0.8rem" }}>
                  {a.edi.segment}
                  {a.edi.qualifier ? `+${a.edi.qualifier}` : ""}/DE{a.edi.data_element} ={" "}
                  {JSON.stringify(a.edi.value)} → {a.bo4e.bo}.{a.bo4e.path} ={" "}
                  {JSON.stringify(a.bo4e.value)}
                </span>
              </Flex>
            ))}
          </Disclosure>

          <Disclosure title={`Übersprungen (${result.skipped.length})`} defaultExpanded={false}>
            {result.skipped.slice(0, 200).map((s, i) => (
              <Flex key={i} style={{ gap: "var(--s-2)", padding: "2px 0", flexWrap: "wrap" }}>
                <span style={{ fontFamily: "var(--sapFontMonoFamily, monospace)", fontSize: "0.8rem" }}>
                  {s.mapping_id}
                </span>
                <span style={{ fontSize: "0.8rem", color: "var(--sapContent_LabelColor)" }}>
                  {s.reason}
                </span>
              </Flex>
            ))}
            {result.skipped.length > 200 && (
              <span style={{ fontSize: "0.8rem" }}>… {result.skipped.length - 200} weitere</span>
            )}
          </Disclosure>
        </>
      )}
    </Stack>
  );
}
